import { Controller } from "@hotwired/stimulus"

// The collections of a cash day ticked one by one as they are checked: what
// is ticked adds up to the amount validated, at the corrected amount when
// one is typed. A line set aside is never ticked. A line of the drawer left
// neither ticked nor set aside holds the form.
export default class extends Controller {
  static targets = ["line", "count", "total", "alert", "grandTotal", "received", "receivedNote"]
  static values = { allLabel: String, noneLabel: String }

  connect() {
    this.refresh()
  }

  refresh() {
    let grand = 0
    let inHand = 0
    this.sections.forEach((section) => {
      let count = 0
      let total = 0
      this.linesOf(section).forEach((line) => {
        const box = this.box(line)
        const aside = line.querySelector("[data-set-aside]")
        if (aside && aside.checked) box.checked = false
        box.disabled = Boolean(aside && aside.checked)
        if (!box.checked) return

        count += 1
        total += this.amountOf(line)
        if (line.hasAttribute("data-in-hand")) inHand += this.amountOf(line)
      })
      this.write(section, "count", String(count))
      this.write(section, "total", this.money(total))
      this.label(section.querySelector("[data-tick-all]"), this.linesOf(section))
      section.querySelectorAll("[data-tick-group]").forEach((button) => {
        this.label(button, this.linesOf(section).filter((line) => line.dataset.group === button.dataset.group))
      })
      grand += total
    })
    if (this.hasGrandTotalTarget) this.grandTotalTarget.textContent = this.money(grand)
    this.fillReceived(inHand)
    // The question a partial validation asks is about what is ticked, not about the whole.
    this.element.querySelectorAll("[data-partial]").forEach((button) => { button.dataset.confirmAmount = this.money(grand) })
    if (this.unchecked.length === 0) this.alertTargets.forEach((alert) => { alert.hidden = true })
  }

  // The amount received follows what is ticked among the cash and the
  // cheques — mobile money is on its account, never handed over —, never
  // above what was declared, and no longer once it is typed by hand.
  fillReceived(inHand) {
    if (!this.hasReceivedTarget) return

    const declared = parseInt(this.receivedTarget.max, 10) || 0
    if (!this.receivedTyped) this.receivedTarget.value = inHand > 0 ? Math.min(inHand, declared) : declared
    this.inHand = inHand
    this.noteReceived()
  }

  typed() {
    this.receivedTyped = true
    this.noteReceived()
  }

  // Received below what was declared is a shortfall someone answers for: said before it is sent.
  noteReceived() {
    if (!this.hasReceivedNoteTarget) return

    const note = this.receivedNoteTarget
    const declared = parseInt(this.receivedTarget.max, 10) || 0
    const received = parseInt(this.receivedTarget.value, 10) || 0
    const lines = []
    if (received < declared) lines.push(note.dataset.short.replace("%{amount}", this.money(declared - received)).replace("%{declared}", this.money(declared)))
    if (this.inHand > declared) lines.push(note.dataset.capped.replace("%{ticked}", this.money(this.inHand)).replace("%{declared}", this.money(declared)))
    note.textContent = lines.join(" ")
    note.hidden = lines.length === 0
  }

  toggleAll(event) {
    this.toggle(this.linesOf(event.currentTarget.closest("[data-day-lines]")))
  }

  toggleGroup(event) {
    const button = event.currentTarget
    this.toggle(this.linesOf(button.closest("[data-day-lines]")).filter((line) => line.dataset.group === button.dataset.group))
  }

  // Held while a line of the drawer is neither ticked nor set aside; a
  // button that refuses or validates one paper alone is not concerned.
  guard(event) {
    if (event.submitter && event.submitter.hasAttribute("formaction")) return
    // Validated in part: only what is ticked, the rest waits.
    if (event.submitter && event.submitter.hasAttribute("data-partial")) {
      // ... and the money is received whole, at what was declared.
      if (this.hasReceivedTarget) this.receivedTarget.value = this.receivedTarget.max
      return
    }

    const first = this.unchecked[0]
    if (!first) return

    event.preventDefault()
    event.stopImmediatePropagation()
    const alert = first.closest("[data-day-lines]").querySelector("[data-cash-check-target='alert']")
    if (alert) alert.hidden = false
    first.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  get sections() {
    return Array.from(this.element.querySelectorAll("[data-day-lines]"))
  }

  get unchecked() {
    return this.lineTargets.filter((line) => {
      const aside = line.querySelector("[data-set-aside]")
      return line.hasAttribute("data-required") && !this.box(line).checked && !(aside && aside.checked)
    })
  }

  linesOf(section) {
    return this.lineTargets.filter((line) => section.contains(line))
  }

  box(line) {
    return line.querySelector("[data-tick]")
  }

  amountOf(line) {
    const corrected = line.querySelector("[data-corrected-amount]")
    const typed = corrected ? parseInt(corrected.value, 10) : NaN
    const amount = parseInt(line.dataset.amount, 10) || 0
    return typed > 0 && typed < amount ? typed : amount
  }

  toggle(lines) {
    const free = lines.filter((line) => !this.box(line).disabled)
    const all = free.every((line) => this.box(line).checked)
    free.forEach((line) => { this.box(line).checked = !all })
    this.refresh()
  }

  label(button, lines) {
    if (!button) return

    const free = lines.filter((line) => !this.box(line).disabled)
    button.textContent = free.length > 0 && free.every((line) => this.box(line).checked) ? this.noneLabelValue : this.allLabelValue
  }

  write(section, name, text) {
    section.querySelectorAll(`[data-cash-check-target='${name}']`).forEach((node) => { node.textContent = text })
  }

  money(amount) {
    return `${String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, " ")} F CFA`
  }
}
